import React from 'react'
import { Box, makeStyles, Typography, Grid } from '@material-ui/core'
import PropTypes from 'prop-types'

const useStyles = makeStyles(theme => ({
    root: {
        color: '#e8ecea!important',
        fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif'
    },
    upperSection: {
        background: 'linear-gradient(180deg, #0f1214 0%, #151a1c 100%)',
        padding: theme.spacing(8, 0, 7, 0),
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)'
        }
    },
    lowerSection: {
        background: '#0c0e10',
        padding: theme.spacing(3, 0),
        borderTop: '1px solid rgba(255, 255, 255, 0.04)',
        '& a': {
            marginRight: theme.spacing(2.5),
            position: 'relative',
            '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -2,
                left: 0,
                width: 0,
                height: 1,
                background: theme.palette.primaryColor || '#4a9d7a',
                transition: 'width 0.25s ease'
            },
            '&:hover::after': {
                width: '100%'
            }
        }
    },
    wrapper: {
        padding: theme.spacing(0, 3),
        maxWidth: '76.875rem',
        margin: 'auto',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(0, 2)
        }
    },
    logoWrapper: {
        marginBottom: theme.spacing(3)
    },
    logo: {
        maxWidth: '100%',
        width: 250,
        opacity: 0.95,
        filter: 'brightness(1.05)'
    },
    headerWrapper: {
        marginBottom: theme.spacing(2)
    },
    header: {
        display: 'inline-block',
        textAlign: 'left',
        color: '#ffffff!important',
        fontSize: '1rem!important',
        fontWeight: '600!important',
        letterSpacing: '0.3px',
        position: 'relative',
        paddingBottom: theme.spacing(1),
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: 32,
            height: 2,
            background: `linear-gradient(90deg, ${theme.palette.primaryColor || '#4a9d7a'} 0%, transparent 100%)`,
            borderRadius: 1
        }
    },
    content: {
        color: '#a8b5ae!important',
        marginBottom: theme.spacing(1.5),
        fontSize: '0.9rem',
        lineHeight: 1.65,
        '& a': {
            color: (theme.palette.primaryColor || '#4a9d7a') + '!important',
            textDecoration: 'none',
            fontWeight: 500,
            transition: 'opacity 0.2s ease',
            '&:hover': {
                opacity: 0.8
            }
        }
    },
    sectionBox: {
        padding: theme.spacing(2.5),
        background: 'rgba(255, 255, 255, 0.02)',
        borderRadius: 16,
        border: '1px solid rgba(255, 255, 255, 0.04)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        marginBottom: theme.spacing(2),
        transition: 'border-color 0.25s ease, background 0.25s ease',
        '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.08)',
            background: 'rgba(255, 255, 255, 0.025)'
        }
    },
    descriptionContent: {
        color: '#8a9a92!important',
        fontSize: '0.88rem',
        lineHeight: 1.7,
        marginBottom: theme.spacing(2),
        '& a': {
            color: (theme.palette.primaryColor || '#4a9d7a') + '!important',
            textDecoration: 'none',
            fontWeight: 500,
            transition: 'opacity 0.2s ease',
            '&:hover': {
                opacity: 0.8
            }
        }
    },
    footerLinks: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        '& a': {
            color: '#8a9a92!important',
            textDecoration: 'none',
            fontSize: '0.85rem',
            fontWeight: 500,
            transition: 'color 0.2s ease',
            '&:hover': {
                color: '#ffffff!important'
            }
        }
    },
    copyright: {
        color: '#5a6a62!important',
        fontSize: '0.82rem',
        textAlign: 'right',
        [theme.breakpoints.down('sm')]: {
            textAlign: 'left',
            marginTop: theme.spacing(2)
        }
    },
    gridContainer: {
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column'
        }
    }
}))

function Footer({ className, children, icon, ...rest }) {
    const classes = useStyles()

    return (
        <Box className={classes.root + ' footer'}>
            <Box className={classes.upperSection}>
                <Box className={classes.wrapper}>
                    <Grid container spacing={4} className={classes.gridContainer}>
                        <Grid item xs={12} md={7}>
                            <Box className={classes.logoWrapper}>
                                <img src="https://uusi.kilpailu.fi/images/tutkimuskeskus-2026-full-green-icon-white.svg" className={classes.logo} alt="Tutkimuskeskus" />
                            </Box>
                            <Typography variant="body1" className={classes.descriptionContent}>
                                Tutkimuskeskus - TTK on 100% suomalainen tutkimussivusto. Keräämme anonyymia tutkimustietoa eri toimialojen yrityksille. Tutkimukseen osallistuminen on aina ilmaista etkä sitoudu mihinkään. Palkinnot tarjoaa yhteistyökumppanimme tai LeadSpark Oy.
                            </Typography>
                            <Typography variant="body1" className={classes.descriptionContent}>
                                Voittaja arvotaan kilpailun päätyttyä. Voittajaan ollaan yhteydessä sähköpostitse ja puhelimitse. Kun voittaja on
                                arvottu ja tavoitettu, ilmoitamme siitä Tutkimuskeskus-sivustolla anonyymisti.
                            </Typography>
                            <Typography variant="body1" className={classes.descriptionContent} style={{ marginBottom: 0 }}>
                                Tutkimuskeskuksen palvelusta vastaa suomalainen teknologiayritys LeadSpark Oy, joka on rekisteröity
                                kaupparekisteriin Y-tunnuksella 2775435-5.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={1} />
                        <Grid item xs={12} md={4}>
                            <Box className={classes.sectionBox}>
                                <Box className={classes.headerWrapper}>
                                    <Typography variant="h6" className={classes.header}>
                                        Asiakaspalvelu
                                    </Typography>
                                </Box>
                                <Typography variant="body2" className={classes.content}>
                                    Palvelemme arkisin klo 9-17.
                                </Typography>
                                <Typography variant="body2" className={classes.content} style={{ marginBottom: 0 }}>
                                    Tavoitat meidät sähköpostitse{' '}
                                    <a target="_blank" rel="noopener noreferrer" href="mailto:contact@leadspark.io">
                                        contact@leadspark.io
                                    </a>
                                </Typography>
                            </Box>
                            <Box className={classes.sectionBox}>
                                <Box className={classes.headerWrapper}>
                                    <Typography variant="h6" className={classes.header}>
                                        Tietosuoja ja ehdot
                                    </Typography>
                                </Box>
                                <Typography variant="body2" className={classes.content}>
                                    Voit tutustua palvelumme tietosuoja- ja evästekäytäntöihin sekä käyttöehtoihin.
                                </Typography>
                                <Typography variant="body2" className={classes.content} style={{ marginBottom: 0 }}>
                                    <a target="_blank" rel="noopener noreferrer" href="https://www.tutkimuskeskus.fi/privacy-policy?cid=74">
                                        Tietosuojaseloste
                                    </a>{' '}in
                                    &{' '}
                                    <a target="_blank" rel="noopener noreferrer" href="https://www.tutkimuskeskus.fi/terms?cid=74">
                                        Käyttöehdot
                                    </a>
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Box className={classes.lowerSection}>
                <Box className={classes.wrapper}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Box className={classes.footerLinks}>
                                <a target="_blank" rel="noopener noreferrer" href="https://www.leadspark.fi/">
                                    LeadSpark
                                </a>
                                <a target="_blank" rel="noopener noreferrer" href="https://www.tutkimuskeskus.fi">
                                    Tutkimuskeskus
                                </a>
                                <a target="_blank" rel="noopener noreferrer" href="https://www.tutkimuskeskus.fi">
                                    Voittajat
                                </a>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="body2" className={classes.copyright}>
                                © {new Date().getFullYear()} LeadSpark. All Rights Reserved.
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    )
}

Footer.propTypes = {
    className: PropTypes.string
}

export default Footer
